import { type User as OAuthUser, useAuth0 } from "@auth0/auth0-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import classNames from "classnames";
import { LDProvider, useFlags } from "launchdarkly-react-client-sdk";
import { type ReactNode, useState } from "react";
import { isMobile } from "react-device-detect";
import { Route } from "react-router";
import { BrowserRouter, Routes, useSearchParams } from "react-router-dom";
import { Button } from "./components/Button/Button.tsx";
import { Gmail } from "./components/Icon/assets/Gmail.tsx";
import { Outlook } from "./components/Icon/assets/Outlook.tsx";
import { Pimento } from "./components/Icon/assets/Pimento.tsx";
import { Spinner } from "./components/Spinner/Spinner.tsx";
import { TooltipProvider } from "./components/Tooltip/Tooltip.tsx";
import { VersionBanner } from "./components/Version/VersionBanner.tsx";
import { useOnMount } from "./hooks/useOnMount.ts";
import { useAppMutation } from "./http/useAppMutation.ts";
import { useAppSubscription } from "./http/useAppSubscription.ts";
import {
  BOARDS,
  MODELS,
  PAYMENT_CONFIRMATION,
  USER_ONBOARDING,
  WORKSPACES,
} from "./routes.ts";
import { AuthProvider } from "./singletons/AuthProvider.tsx";
import { ErrorBoundary } from "./singletons/ErrorBoundary.tsx";
import { NotificationsContainer } from "./singletons/NotificationsContainer/NotificationsContainer.tsx";
import { pushGTMUserData } from "./tracking/gtm/pushGTMUserData.ts";
import { initTracking, trackEvent } from "./tracking/tracking.ts";
import {
  expireUtmParamsCookie,
  getUtmParamsFromCookies,
} from "./tracking/utm.ts";
import { initIntercom } from "./utils/intercom.ts";
import {
  BoardRedirect,
  GetExistingOrCreateBoardRedirect,
} from "./views/Board/BoardRedirect.tsx";
import { useDebugMode } from "./views/components/DebugMode/hooks/useDebugMode.ts";
import { useUser } from "./views/hooks/useUser.ts";
import { Maintenance } from "./views/Maintenance.tsx";
import { MobileView } from "./views/Mobile.tsx";
import { ModelRedirect } from "./views/Model/ModelRedirect.tsx";
import { PaymentConfirmationView } from "./views/PaymentConfirmation/PaymentConfirmationView.tsx";
import { UserOnboardingRedirect } from "./views/UserOnboarding/UserOnboardingRedirect.tsx";
import { UserOnboardingView } from "./views/UserOnboarding/UserOnboardingView.tsx";
import {
  WorkspaceRedirect,
  WorkspaceView,
} from "./views/Workspace/Workspace.tsx";
/**
 * This should be the last import to allow tailwind utilities to override any previously imported css file
 */
import "./index.css";

export const App = () => {
  const [queryClient] = useState(() => new QueryClient());

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export const AppRouter = () => (
  <AuthProvider>
    <TooltipProvider delayDuration={500}>
      <NotificationsContainer />
      <AuthSwitch />
    </TooltipProvider>
  </AuthProvider>
);

const EMAIL_VERIFICATION_ERROR_PREFIX =
  "Please verify your email before continuing. email: ";
const AuthSwitch = () => {
  const [searchParams] = useSearchParams();
  const { user, isLoading, error, loginWithRedirect, logout } = useAuth0();

  if (isLoading) {
    return (
      <div className="w-full h-full flex-col-center">
        <Spinner />
      </div>
    );
  }

  if (error && user === undefined) {
    return (
      <div className=" w-full h-full bg-surface-secondary-rest py-800 flex-col items-center justify-between">
        <Pimento size={100} />
        <div className="rounded-300 w-[392px] p-800 flex-col bg-surface-primary-rest">
          {error.message.startsWith(EMAIL_VERIFICATION_ERROR_PREFIX) ? (
            <div className="flex-fill flex-col-center gap-600">
              <div className="flex-col-center gap-200">
                <div className="heading-4xl">Check your email</div>
                <div className="body-lg-default text-center">
                  We’ve sent a confirmation link to{" "}
                  <span className="body-lg-semibold">
                    {error.message.replace(EMAIL_VERIFICATION_ERROR_PREFIX, "")}
                  </span>
                  , click on it to verify your email and keep going
                </div>
              </div>
              <div className="flex-col-center gap-200">
                <div className="flex-row-center">
                  <a
                    href="https://mail.google.com/"
                    className="p-300 flex-row-center gap-050 rounded-border-radius-button hover:bg-button-surface-tertiary-hover"
                  >
                    <Gmail />
                    <div className="label-md-semibold">Open Gmail</div>
                  </a>
                  <a
                    href="https://outlook.live.com/mail/0/"
                    className="p-300 flex-row-center gap-050 rounded-border-radius-button hover:bg-button-surface-tertiary-hover"
                  >
                    <Outlook />
                    <div className="label-md-semibold">Open Outlook</div>
                  </a>
                </div>
                <div className="body-sm-default text-secondary text-center">
                  Didn’t receive the email? Check your spam folder or write to
                  us at&nbsp;
                  <a href="mailto:contact@pimento.design" className="underline">
                    contact@pimento.design
                  </a>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-col-center gap-200">
              <span className="text-error-secondary">{error.message}</span>
              <span>Are you using the right account?</span>
              <span className="text-secondary">
                If you need further assistance, you may contact us on
                contact@pimento.design
              </span>
            </div>
          )}
        </div>
        <Button
          variant="tertiary"
          onClick={() => logout()}
          iconName="ArrowLeft"
        >
          Back to login
        </Button>
      </div>
    );
  }

  if (!user) {
    void loginWithRedirect({
      appState: { returnTo: window.location.pathname + window.location.search },
      screen_hint:
        searchParams.get("auth_flow") === "signup" ? "signup" : undefined,
    });

    return null;
  }

  return (
    <UserSync>
      <AuthApp oauthUser={user} />
    </UserSync>
  );
};

// XXX: the goal of this component is to sync the user with the backend before rendering anything else
// if the user is unknown in the backend which happens the first time they log in the ws and boards route calls will fail
const UserSync = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, logout } = useAuth0();
  const [userSyncState, setUserSyncState] = useState<
    "LOADING" | "SUCCESS" | "ERROR"
  >("LOADING");
  const utmParams = getUtmParamsFromCookies();
  const loginMutation = useAppMutation<{
    uuid: string;
    email: string;
    created_at: string;
    phone_number: string | null;
    first_name: string | null;
    last_name: string | null;
    occupation: string | null;
    intercom_user_hash: string;
    is_new_user: boolean;
  }>({
    path: "users/login",
    invalidate: [],
    onSuccess: ({ data }) => {
      setUserSyncState("SUCCESS");
      initTracking(data);
      // XXX:Wait on the data to be pushed to dataLayer before tracking the event,
      // otherwise there is a race between the data being pushed to dataLayer,
      // and the event being tracked, leading to the event missing the data.
      void (async () => {
        await pushGTMUserData({
          email: data.email,
          firstName: data.first_name,
          lastName: data.last_name,
          jobTitle: data.occupation,
          phoneNumber: data.phone_number,
        });
        if (data.is_new_user) {
          trackEvent("user:signup", utmParams ?? {}, true);
        } else {
          trackEvent("user:login", utmParams ?? {}, true);
        }
        // Only expire the utm params cookie if the login was successful,
        // otherwise the UTM params will be lost and the user will not be able to
        // be tracked in the next login.
        if (utmParams) expireUtmParamsCookie();
      })();
      initIntercom({
        userEmail: data.email,
        userUuid: data.uuid,
        userCreatedAt: data.created_at,
        userName: `${data.first_name ?? ""} ${data.last_name ?? ""}`,
        userHash: data.intercom_user_hash,
      });
    },
    onError: () => {
      setUserSyncState("ERROR");
    },
  }).mutation;

  useOnMount(() => {
    loginMutation.mutate({
      utm_params: utmParams ?? null,
    });
  });

  return userSyncState === "ERROR" ? (
    <div className="w-full h-full flex-col-center gap-600">
      <div>
        Failed login: it looks like we couldn't find you in our records. Please
        try again.
      </div>
      <div className="flex-row-center gap-400">
        <Button
          onClick={() =>
            loginMutation.mutate({
              utm_params: utmParams ?? null,
            })
          }
        >
          Retry
        </Button>
        {isAuthenticated && <Button onClick={() => logout()}>Logout</Button>}
      </div>
    </div>
  ) : (
    userSyncState === "SUCCESS" && children
  );
};

const AuthApp = ({ oauthUser }: { oauthUser: OAuthUser }) => {
  useAppSubscription();

  return (
    <LDProvider
      clientSideID="6464dbe8b4e764137e97340f"
      context={{
        kind: "user",
        key: oauthUser.sub,
        firstName: oauthUser.given_name,
        email: oauthUser.email,
      }}
    >
      <AuthRouter />
    </LDProvider>
  );
};

const AuthRouter = () => {
  const flags = useFlags();
  const { user } = useUser();
  const isDebugModeOn = useDebugMode();

  if (flags["maintenance"]) {
    return (
      <Routes>
        <Route path="*" element={<Maintenance />} />
      </Routes>
    );
  }
  if (isMobile) {
    return (
      <Routes>
        <Route path="*" element={<MobileView />} />
      </Routes>
    );
  }

  if (!user?.onboarding_finished_at) {
    // XXX: We force the redirection to a specific route to be sure the onboarding is diplayed with the correct url so we can filter it out in Intercom and other external tools
    return (
      <div className="flex-fill flex-col">
        <Routes>
          <Route path="*" element={<UserOnboardingRedirect />} />
          <Route path={USER_ONBOARDING} element={<UserOnboardingView />} />
        </Routes>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        "relative flex-fill flex-col bg-surface-primary-rest",
        isDebugModeOn && "border-150 border-caution-rest",
      )}
    >
      <VersionBanner />
      <Routes>
        <Route
          path={PAYMENT_CONFIRMATION}
          element={<PaymentConfirmationView />}
        />
        <Route
          path={`${WORKSPACES}/:workspaceUuid/*`}
          element={<WorkspaceView />}
        />
        <Route path={`${MODELS}/:modelUuid/*`} element={<ModelRedirect />} />
        <Route path={BOARDS} element={<BoardRedirect />} />
        <Route
          path={`${WORKSPACES}/:workspaceUuid/${BOARDS}`}
          element={<GetExistingOrCreateBoardRedirect />}
        />
        <Route path="*" element={<WorkspaceRedirect />} />
      </Routes>
    </div>
  );
};
